import { ITimelineAsset } from "@/types/assetTypes";
import { Category } from './TimelineCategories/types';
import { TRIGGER_COLOR_DEEP } from '@/modules/triggerAuthoring/triggerColors';
import { themeColour, themeFont, getTheme } from '@/modules/theme';

// The timeline's image cursors (lock, no entry, blade): each is square with its hotspot at the centre, its stroke in the icon's 256 grid
export const CURSOR_PX = 20;
export const CURSOR_STROKE = 16;
export const BLADE_CURSOR_PX = 22;
export const BLADE_CURSOR_STROKE = 18;

// Phosphor's lock, and a no-entry sign (circle with diagonal line), as cursors in the theme's cursor ink, built once per theme
const LOCK_PATH = 'M208,80H176V56a48,48,0,0,0-96,0V80H48A16,16,0,0,0,32,96V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V96A16,16,0,0,0,208,80ZM96,56a32,32,0,0,1,64,0V80H96ZM208,208H48V96H208V208Z';
const stateCursors = new Map<string, { lock: string; noEntry: string }>();

const svgCursor = (svg: string, fallback: string) => `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${CURSOR_PX / 2} ${CURSOR_PX / 2}, ${fallback}`;

/** The cursor over a locked or a hidden asset, in the current theme. */
export function stateCursor(kind: 'lock' | 'noEntry') {
  const theme = getTheme();
  let cursors = stateCursors.get(theme);
  if (!cursors) {
    const ink = themeColour('lureo-ink-subtle');
    const open = `<svg xmlns='http://www.w3.org/2000/svg' width='${CURSOR_PX}' height='${CURSOR_PX}' viewBox='0 0 256 256' fill='${ink}'>`;
    cursors = {
      lock: svgCursor(`${open}<path d='${LOCK_PATH}'/></svg>`, 'pointer'),
      noEntry: svgCursor(
        `${open}<circle cx='128' cy='128' r='100' fill='none' stroke='${ink}' stroke-width='${CURSOR_STROKE}'/><line x1='60' y1='60' x2='196' y2='196' stroke='${ink}' stroke-width='${CURSOR_STROKE}' stroke-linecap='round'/></svg>`,
        'not-allowed'
      ),
    };
    stateCursors.set(theme, cursors);
  }
  return cursors[kind];
}

/**
 * Initial/minimum timeline canvas length in seconds.
 * The live canvas length lives in React state (see useTimelineController.timelineLength)
 * and grows when assets extend past it. Never shrinks during a session.
 */
export const INITIAL_TIMELINE_LENGTH_SECONDS = 60;

// Zoom, in pixels per second: its range, the step the buttons, keys and slider move by, and where a reset lands
export const TIMELINE_ZOOM_MIN = 15;
export const TIMELINE_ZOOM_MAX = 120;
export const TIMELINE_ZOOM_STEP = 15;
export const TIMELINE_ZOOM_DEFAULT = 60;

/**
 * Timescale tick increment for a given zoom scale.
 * Scale range: TIMELINE_ZOOM_MIN (most zoomed out) to TIMELINE_ZOOM_MAX (most zoomed in).
 */
export const getTimeIncrement = (scale: number): number => {
  if (scale < 25) return 1;
  if (scale < 40) return 0.5;
  if (scale < 55) return 0.2;
  if (scale < 75) return 0.1;
  if (scale < 100) return 0.05;
  return 0.01;
};

// The dock's height in vh: it starts at 0 so it never jumps to a big default before auto-fit runs; the min is a fallback, the real one is measured from the header
export const DEFAULT_HEIGHT_VH = 0;
export const MAX_HEIGHT_VH = 55;
export const MIN_HEIGHT_VH = 3;
/** Gap (px) between the top of the timeline and the bottom of the side menu. Increase if timeline overlaps the menu. */
export const TIMELINE_SIDEBAR_GAP_PX = 16;

// Every z-index the timeline sets, grouped by the stack it sits in, lowest first in each
export const TIMELINE_LAYERS = {
  // The dock, over its scroll area
  scrollArea: 1,
  playingOverlay: 25,
  categoryPill: 40,
  categoryPillDragging: 50,
  dockResizeGrip: 50,
  // A lane's name pill at rest (the sidebar copy and the lane's own), and the sidebar copy opened
  lanePill: 60,
  sidebarPillOpen: 100,
  // The scroll area's frozen ruler, triggers row, lane column and corner
  ruler: 20,
  triggersRow: 20,
  triggerLaneHeader: 21,
  laneColumnGrip: 30,
  laneColumn: 41,
  corner: 50,
  // Scrolled to the start, the lanes, triggers row and ruler draw over the lane column and corners, so marks at 0 show whole
  lanesAtStart: 42,
  triggersRowAtStart: 48,
  rulerAtStart: 52,
  // Inside the ruler, a dragged item's time labels over the marks
  rulerDragLabel: 100,
  // The lanes' content
  gateLine: -1,
  runningGateLine: 11,
  gateWash: 0,
  laneBar: 1,
  laneBarSelected: 10,
  dropGhost: 49,
  dropGhostTarget: 50,
  laneBarDragging: 9999,
  // Inside a lane bar
  rowGateLine: 0,
  unplayedWash: 0,
  keyframeLine: 0,
  clip: 1,
  laneEdgeHandle: 2,
  keyframeLineSelected: 3,
  clipSelected: 10,
  laneStateOverlay: 10,
  insertMarker: 10,
  speedBadge: 10,
  playbackFront: 15,
  keyframes: 15,
  keyframesEditing: 20,
  bladeLine: 30,
  laneConnector: 50,
  pickTarget: 50,
  pendingMarker: 50,
  reactivePill: 110,
  lanePillOpen: 120,
  // Inside a clip
  clipStraddle: 0,
  clipLabel: 10,
  clipEdgeHandle: 20,
  // Inside the triggers row; the playhead's stretch here sits under the markers, which are clicked most
  gateDescend: 1,
  rowPlayhead: 15,
  gateMarker: 20,
  gateMarkerRaised: 30,
  // Inside the insert marker, its glyph over its disc
  insertMarkerGlyph: 10,
} as const;

// The timeline's motion: CSS keyword easings are kept as written, since Tailwind's ease classes are other curves
export const TIMELINE_MOTION = {
  dockHeight: 'height var(--lureo-duration-slower) var(--lureo-easing-standard), max-height var(--lureo-duration-slower) var(--lureo-easing-standard)',
  lanePillWidth: 'width var(--lureo-duration-slower) var(--lureo-easing-standard), min-width var(--lureo-duration-slower) var(--lureo-easing-standard)',
  lanePillLeaveDelayMs: 80,
  // A lane bar's, clip's or edge handle's glide while it resizes: `width ${resizeGlide}`
  resizeGlide: 'var(--lureo-duration-slow) var(--lureo-easing-enter)',
  filterFade: 'filter var(--lureo-duration-moderate)',
  categoryPill: 'transform var(--lureo-duration-moderate) var(--lureo-easing-standard), opacity var(--lureo-duration-moderate) var(--lureo-easing-standard), right var(--lureo-duration-moderate) var(--lureo-easing-standard), border var(--lureo-duration-moderate) var(--lureo-easing-standard)',
  gateMarkerHover: 'transform var(--lureo-duration-fast) var(--lureo-easing-enter)',
  // A waiting gate pulses between floor and floor + swing, once a period
  gatePulsePeriodS: 1,
  gatePulseFloor: 0.45,
  gatePulseSwing: 0.55,
  screenUiStack: 'gap var(--lureo-duration-moderate) var(--lureo-easing-standard)',
  blockedTooltipLingerMs: 500,
  keyframeLineDrawMs: 300,
} as const;

// The ruler row, in pixels, as production's: the corner's 22px search with room above and below it
export const TIMELINE_SCALE_HEIGHT = 32;
// Where the ruler's marks and labels sit, down its height
export const RULER_MARK_TOP = '58%';
// The playhead: its grab strip (centred on the time), its line, its house handle, and how far the line tucks under the handle's glyph
export const PLAYHEAD_GRAB_PX = 20;
export const PLAYHEAD_GRAB_HALF_PX = PLAYHEAD_GRAB_PX / 2;
export const PLAYHEAD_LINE_PX = 2;
export const PLAYHEAD_HANDLE_PX = 24;
export const PLAYHEAD_HANDLE_GLYPH_INSET_PX = 5;
export const TIMELINE_GUTTER_DEFAULT_PX = 144;
export const TIMELINE_GUTTER_MIN_PX = 112;
export const TIMELINE_GUTTER_MAX_PX = 360;
// The lane column's resize grip sits this far inside the playhead's grab strip
export const GUTTER_GRIP_GAP_PX = 2;
// The +/- column at the end of the timeline
export const EXTEND_COLUMN_WIDTH = 56;
// Dedicated scene-wide triggers lane, sits directly under the ruler. Tall enough
// to carry a marker with its type glyph inside and a static stack offset.
export const TRIGGER_ROW_HEIGHT = 30; // in pixels
export const ITEM_HEIGHT = 25; // in pixels
export const LANE_PADDING_Y = 10; // Vertical padding within each lane to center assets
export const LANE_PADDING_Y_COLLAPSED = 2; // Reduced padding for collapsed lanes (adjustable)
// Time buffer (seconds, roughly 40px at the default zoom) that keeps two assets on one row from touching
export const TIME_BUFFER = 0.65;

// Gradient (not dashed border) so the per-row and gap segments phase-lock into one line: a dash, then a gap, every period
export const GATE_LINE_DASH = 3;
export const GATE_LINE_PERIOD = 6;
export const GATE_LINE_GRADIENT =
  `repeating-linear-gradient(180deg, ${TRIGGER_COLOR_DEEP} 0, ${TRIGGER_COLOR_DEEP} ${GATE_LINE_DASH}px, transparent ${GATE_LINE_DASH}px, transparent ${GATE_LINE_PERIOD}px)`;
// The same dashes run across a lane, for the line from a trigger to an asset that appears after it
export const GATE_LINE_GRADIENT_ACROSS =
  `repeating-linear-gradient(90deg, ${TRIGGER_COLOR_DEEP} 0, ${TRIGGER_COLOR_DEEP} ${GATE_LINE_DASH}px, transparent ${GATE_LINE_DASH}px, transparent ${GATE_LINE_PERIOD}px)`;
export const GATE_LINE_GRADIENT_INERT =
  `repeating-linear-gradient(180deg, var(--lureo-grey) 0, var(--lureo-grey) ${GATE_LINE_DASH}px, transparent ${GATE_LINE_DASH}px, transparent ${GATE_LINE_PERIOD}px)`;
// A gate line is the theme's medium line, centred on its time
export const GATE_LINE_WIDTH = 'var(--lureo-border-width-medium)';
export const GATE_LINE_CENTRING = 'translateX(calc(var(--lureo-border-width-medium) * -0.5))';
// A clip keeps this far (px) from a gate on its lane
export const GATE_CLIP_GAP_PX = 10;
/**
 * Pixel distance within which a snap will engage. Anything farther passes
 * through unsnapped so the drag doesn't feel sticky across large gaps.
 */
export const SNAP_THRESHOLD_PX = 16;

// How far the lanes may draw left of 0 over the lane column at the start: the widest mark's half, a selected reactive clip's ring
export const ZERO_REVEAL_PX = 12;

// A gate's octagon in the triggers row: its size, its glyph, its outline, its hover growth, and the click distance that picks it
export const GATE_MARK_PX = 20;
export const GATE_MARK_GLYPH_PX = 11;
export const GATE_MARK_STROKE_PX = 1.5;
export const GATE_MARK_HOVER_SCALE = 1.1;
export const GATE_HIT_GAP_PX = 12;
// Gates closer than a mark's width overlap and stack, a step apart at most, within the room the row leaves around a mark
export const GATE_CLUSTER_PX = GATE_MARK_PX - 2;
export const GATE_STACK_STEP_PX = 10;
export const GATE_STACK_SLACK_PX = (TRIGGER_ROW_HEIGHT - GATE_MARK_PX) / 2;
// The pending edit's octagon on a lane, at the clicked moment
export const PENDING_MARKER_PX = 15;
// A reactive clip's pill on a gate line: its width, its inset from the lane's top and bottom, its corner, its rings when picked, and its glyph
export const REACTIVE_PILL_PX = 16;
export const REACTIVE_PILL_INSET_PX = 2;
export const REACTIVE_PILL_RADIUS = 'calc(var(--lureo-radius) * 0.4167)';
export const REACTIVE_PILL_RING_GAP_PX = 2;
export const REACTIVE_PILL_RING_PX = 3.5;
export const REACTIVE_PILL_GLYPH_PX = 11;
// Clips pushed clear of a reactive gate keep a pill's half-width (8) and 8 more of room from it
export const REACTIVE_GATE_CLEARANCE_PX = 16;

// The root size the lane pill's maths were written at (rem-based spacing grows and shrinks with the viewport's root size)
const DEFAULT_ROOT_PX = 16;
// A lane's name pill at rest: its width, and the side paddings and expand buffer its label is measured with
export const LANE_PILL_REST_WIDTH_PX = 72;
export const LANE_PILL_MEASURE_PAD_LEFT_PX = 6;
export const LANE_PILL_MEASURE_PAD_RIGHT_PX = 4;
export const LANE_PILL_EXPAND_BUFFER_PX = 16;
// The lane's own pill sits 5.5rem (spacing step 22) left of its lane's start, a pixel above it; its connector takes the offset at the default root
export const LANE_PILL_OFFSET_REM = 5.5;
export const LANE_PILL_OFFSET_PX = LANE_PILL_OFFSET_REM * DEFAULT_ROOT_PX;
export const LANE_PILL_NUDGE_UP_PX = 1;
// An asset that appears after its trigger: the gate's dot, the room left before the name and after its text, the narrowest gap that still takes the delay's text, and the arrow into the lane
export const DELAY_GATE_DOT_PX = 8;
export const DELAY_GAP_PX = 4;
export const DELAY_NAME_TRAIL_GAP_PX = 10;
export const DELAY_LABEL_MIN_PX = 56;
export const DELAY_ARROW_PX = 6;
// The sidebar copy of a lane's pill shows once the lane's own pill is this far behind the lane column, and sits a little above its row
export const SIDEBAR_PILL_OFFSET_PX = 48;
export const SIDEBAR_PILL_NUDGE_UP_PX = 2.5;
// Pills below this far down the dock are not drawn
export const PILL_MAX_SCREEN_TOP_PX = 800;

// The category pill: its height, its padding inside a lane (spacing step 0.5), the room its buttons and padding take beside the label, and its gap from the dock's right edge
export const CATEGORY_PILL_HEIGHT = 28;
export const PILL_PADDING_PX = 2;
export const CATEGORY_PILL_CHROME_PX = 72;
export const CATEGORY_PILL_TEXT_GAP_PX = 20;
export const CATEGORY_PILL_EDGE_GAP_REM = 0.5;
/**
 * Gets the category height for a row count (keyframes overlay on assets, so they add no height)
 * @returns Height in pixels
 */
export const getCategoryHeight = (rowCount: number, collapsed: boolean = false): number => {
  return calculateCategoryHeight(rowCount, collapsed);
};

// A lane bar's states: locked is partly grey; hidden fades its clips and outlines it with a strong dashed line inside its edge; locked stripes 5px of ink every 15px
export const LOCKED_FILTER = 'grayscale(40%)';
export const HIDDEN_CLIP_OPACITY = 0.7;
export const LANE_HIDDEN_OUTLINE = 'var(--lureo-border-width-strong) dashed color-mix(in oklab, var(--lureo-ink-subtlest) 60%, transparent)';
export const LANE_HIDDEN_OUTLINE_OFFSET = 'calc(var(--lureo-border-width-strong) * -1)';
export const LANE_LOCKED_STRIPES = 'repeating-linear-gradient(45deg, color-mix(in oklab, var(--lureo-ink-subtlest) 30%, transparent), color-mix(in oklab, var(--lureo-ink-subtlest) 30%, transparent) 5px, transparent 5px, transparent 15px)';
// A lane bar's hover stripes, in 20px tiles with a small corner
export const LANE_HOVER_STRIPES = 'linear-gradient(45deg, color-mix(in oklab, var(--lureo-brand-ink) 10%, transparent) 25%, transparent 25%, transparent 50%, color-mix(in oklab, var(--lureo-brand-ink) 10%, transparent) 50%, color-mix(in oklab, var(--lureo-brand-ink) 10%, transparent) 75%, transparent 75%, transparent)';
export const LANE_HOVER_STRIPE_TILE = '20px 20px';
export const LANE_HOVER_RADIUS = 'calc(var(--lureo-radius) * 0.3333)';
// A lane bar's press while picking a trigger's assets
export const LANE_PICK_PRESS = 'active:scale-[0.985]';
// A lane bar's invisible edge handles for resizing, in the pill's corner
export const LANE_EDGE_HANDLE_PX = 14;
export const LANE_PILL_CORNER = 'var(--lureo-radius-pill)';
// A media lane with no frames yet: 4px stripes every 8px
export const MEDIA_PLACEHOLDER_STRIPES = 'repeating-linear-gradient(90deg, color-mix(in oklab, var(--lureo-secondary) 15%, transparent) 0px, color-mix(in oklab, var(--lureo-secondary) 15%, transparent) 4px, transparent 4px, transparent 8px)';
// An audio lane's waveform
export const WAVEFORM_HEIGHT_PX = 30;
export const WAVEFORM_BAR_COLOUR = 'color-mix(in oklab, var(--lureo-ink-strong) 40%, transparent)';
// The playback front on a lane: its line and that line's rounded end
export const PLAYBACK_FRONT_PX = 2;
export const PLAYBACK_FRONT_RADIUS_PX = 1;
// The insert marker sits this far left of the pointer
export const INSERT_MARKER_OFFSET_PX = 6;
// A lane being dragged leaves a ghost where it was and where it lands
export const DROP_GHOST_BORDER = 'var(--lureo-border-width-strong) dashed var(--lureo-ink-subtlest)';
export const DROP_TARGET_BORDER = 'var(--lureo-border-width-strong) dashed var(--lureo-accent)';
export const DROP_TARGET_FILL = 'linear-gradient(to bottom right, color-mix(in oklab, var(--lureo-accent) 40%, transparent) 0%, color-mix(in oklab, var(--lureo-accent) 40%, transparent) 25%, color-mix(in oklab, var(--lureo-sky) 36%, transparent) 80%, color-mix(in oklab, var(--lureo-sky) 40%, transparent) 100%)';

// A clip: its height (a zero-length fade is a line), the width its border takes off its length, the widths it needs before its label and ease icon show, its ease icon, its edge handles, and its stripes while it straddles a gate
export const CLIP_HEIGHT_PX = 22;
export const ZERO_FADE_CLIP_PX = 2;
export const CLIP_BORDER_ALLOWANCE_PX = 2;
export const CLIP_LABEL_MIN_WIDTH_PX = 60;
export const CLIP_ICON_MIN_WIDTH_PX = 30;
export const CLIP_EASE_ICON_PX = 20;
export const CLIP_EDGE_HANDLE_PX = 15;
export const STRADDLE_STRIPES = 'repeating-linear-gradient(45deg, color-mix(in oklab, var(--lureo-coral) 70%, transparent) 0, color-mix(in oklab, var(--lureo-coral) 70%, transparent) 5px, transparent 5px, transparent 10px)';

// A Screen UI element's preview: its tallest, and how far it pulls its neighbours in as it leaves
export const SCREEN_UI_PREVIEW_MAX_HEIGHT_PX = 400;
export const SCREEN_UI_PREVIEW_EXIT_PULL_PX = 8;

export const formatTime = (time: number): string => {
  const floorTime = Math.floor(time);

  if (floorTime < 60) {
    return `${floorTime}s`;
  }
  const minutes = Math.floor(floorTime / 60);
  const seconds = floorTime % 60;
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

export const formatTimeHeader = (time: number) => {
  const t = Math.max(0, time);
  const floorTime = Math.floor(t);
  const minutes = Math.floor(floorTime / 60);
  const seconds = floorTime % 60;
  const decisecond = Math.floor((t % 1) * 10);

  return `${minutes}:${seconds.toString().padStart(2, '0')}.${decisecond}`;
};

export const calcRowAssignments = (
  assets: ITimelineAsset[],
  preservedPositions?: { [key: string]: number }
): {
  rowMap: { [key: string]: number };
  maxRowIndex: number;
} => {
  const rowAssignments: (typeof assets)[] = [];
  const newRowMap: { [key: string]: number } = {};
  
  // If we have preserved positions, new assets go to row 0
  // Simply increment all existing rows by 1 for each new asset
  if (preservedPositions && Object.keys(preservedPositions).length > 0) {
    // Count new assets
    const newAssetCount = assets.filter(a => {
      const id = a.assetInstance?.instanceId;
      return id && preservedPositions[id] === undefined;
    }).length;
    
    // If there are new assets, place them at the top with collision detection,
    // then push existing assets down by the number of top rows used
    if (newAssetCount > 0) {
      // Increment all existing row indices by the number of new assets
      assets.forEach((asset) => {
        const assetId = asset.assetInstance?.instanceId;
        if (!assetId) return;

        if (preservedPositions[assetId] !== undefined) {
          // Existing asset - push down by newAssetCount
          newRowMap[assetId] = preservedPositions[assetId] + newAssetCount;
        }
      });

      // Place new assets at rows 0, 1, 2, etc.
      let newAssetRow = 0;
      assets.forEach((asset) => {
        const assetId = asset.assetInstance?.instanceId;
        if (!assetId) return;

        if (preservedPositions[assetId] === undefined) {
          // New asset - place at top
          newRowMap[assetId] = newAssetRow;
          newAssetRow++;
        }
      });
    } else {
      // No new assets - use preserved positions but compact to remove gaps
      // This handles the case when an asset is deleted
      const usedRows = new Set<number>();
      assets.forEach((asset) => {
        const assetId = asset.assetInstance?.instanceId;
        if (!assetId) return;
        
        if (preservedPositions[assetId] !== undefined) {
          usedRows.add(preservedPositions[assetId]);
        }
      });
      
      // Create a mapping from old row indices to compacted indices
      const sortedRows = Array.from(usedRows).sort((a, b) => a - b);
      const rowCompaction: { [key: number]: number } = {};
      sortedRows.forEach((row, index) => {
        rowCompaction[row] = index;
      });
      
      // Apply compaction
      assets.forEach((asset) => {
        const assetId = asset.assetInstance?.instanceId;
        if (!assetId) return;
        
        if (preservedPositions[assetId] !== undefined) {
          const oldRow = preservedPositions[assetId];
          newRowMap[assetId] = rowCompaction[oldRow] ?? oldRow;
        }
      });
    }
    
    return {
      rowMap: newRowMap,
      maxRowIndex: Math.max(...Object.values(newRowMap), 0)
    };
  }

  // Original logic for initial layout (no preserved positions)
  assets.forEach((asset) => {
    const assetId = asset.assetInstance?.instanceId;
    if (!assetId) return;

    let assignedRow = -1;
    for (let rowIndex = 0; rowIndex < rowAssignments.length; rowIndex++) {
      const rowItems = rowAssignments[rowIndex];

      const hasCollision = rowItems.some((item) => {
        const aStart = asset.startTime || 0;
        const aEnd = asset.endTime || 0;
        const bStart = item.startTime || 0;
        const bEnd = item.endTime || 0;

        // Add a small buffer time for the icon (40px worth of time)
        return !(
          (aEnd <= bStart - TIME_BUFFER) || // Check if asset ends before next item's icon
          (aStart - TIME_BUFFER >= bEnd)     // Check if asset's icon starts after next item
        );
      });

      if (!hasCollision) {
        assignedRow = rowIndex;
        break;
      }
    }

    if (assignedRow === -1) {
      assignedRow = rowAssignments.length;
      rowAssignments.push([]);
    }
    rowAssignments[assignedRow].push(asset);
  });

  rowAssignments.forEach((rowItems, rowIndex) => {
    rowItems.forEach((asset) => {
      const assetId = asset.assetInstance?.instanceId;
      if (assetId) {
        newRowMap[assetId] = rowIndex;
      }
    });
  });

  return {
    rowMap: newRowMap,
    maxRowIndex: rowAssignments.length ? rowAssignments.length - 1 : 0
  };
};

/**
 * Calculates the height of a timeline category based on the number of rows
 * @param rowCount Number of rows in the category
 * @returns Height in pixels
 */
export const calculateCategoryHeight = (rowCount: number, collapsed: boolean = false): number => {
  if (collapsed) {
    // Collapsed size is exactly pill height + padding on both sides
    return CATEGORY_PILL_HEIGHT + (PILL_PADDING_PX * 2);
  }
  
  const paddingY = LANE_PADDING_Y;
  // Standard row height calculation (no extra space at top)
  return (rowCount * (ITEM_HEIGHT + (paddingY / 2))) + (paddingY / 2);
};

/**
 * Calculates the total height of all timeline categories
 * @param categories Array of timeline categories
 * @returns Total height in pixels
 */
export const calculateTotalTimelineHeight = (categories: Category[], collapsedCategories: Set<string>): number => {
  return categories.reduce((total, category) => {
    const isCollapsed = collapsedCategories.has(category.label);
    const rowCount = isCollapsed ? 1 : (category.items.length || 1);
    return total + calculateCategoryHeight(rowCount, isCollapsed);
  }, 0);
};

/**
 * Calculates the vertically centered top position for an asset within its lane
 * @param rowIndex The row index (0-based)
 * @returns Top position in pixels to center the asset in its lane
 */
export const calculateCenteredAssetTop = (rowIndex: number, collapsed: boolean = false): number => {
  const paddingY = collapsed ? LANE_PADDING_Y_COLLAPSED : LANE_PADDING_Y;
  const laneTop = rowIndex * (ITEM_HEIGHT + (paddingY / 2)) + (paddingY / 2); // Normal centering
  return laneTop;
};

/**
 * Calculates the timeline time position from a mouse click event
 * @param event - The mouse event
 * @param assetStartTime - The start time of the asset
 * @param assetDuration - The duration of the asset
 * @returns The calculated time position
 */
export const calculateTimePositionFromClick = (
  event: React.MouseEvent,
  assetStartTime: number,
  assetDuration: number
): number => {
  const rect = event.currentTarget.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const relativeTime = (clickX / rect.width) * assetDuration;
  return assetStartTime + relativeTime;
};

// The theme's interface font, read once per change to the theme
const labelFamilies = new Map<number, string>();
const labelWeights = new Map<number, string>();
const interfaceFont = () =>
  getComputedStyle(document.documentElement).getPropertyValue('--lureo-font-ui').trim() || themeFont('system-ui');
const labelWeight = () => getComputedStyle(document.documentElement).getPropertyValue('--lureo-weight-label').trim() || '500';

/** The canvas font pill labels (text-lureo-12 at the label weight) are measured in: the theme's interface font at its label weight and 0.75 of the root size of the document showing them, which follows the viewport (useThemeVersion's number, so a theme change measures again). */
export const labelFont = (themeVersion: number, doc: Document = document) => {
  if (!labelFamilies.has(themeVersion)) labelFamilies.set(themeVersion, interfaceFont());
  if (!labelWeights.has(themeVersion)) labelWeights.set(themeVersion, labelWeight());
  const rootPx = parseFloat(getComputedStyle(doc.documentElement).fontSize) || 16;
  return `${labelWeights.get(themeVersion)} ${0.75 * rootPx}px ${labelFamilies.get(themeVersion)}`;
};
